import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Star, Zap } from "lucide-react";
import { useLang } from "../../context/LanguageContext";

/**
 * Sticky booking CTA — appears after the user scrolls past the hero.
 * Shows headline price + rating + book button. Hidden on /book and /admin routes.
 */
export default function StickyBookingBar() {
  const { lang } = useLang();
  const { pathname } = useLocation();
  const [visible, setVisible] = useState(false);
  const [closed, setClosed] = useState(false);

  useEffect(() => {
    if (pathname !== "/") { setVisible(false); return; }
    const onScroll = () => setVisible(window.scrollY > 700);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [pathname]);

  if (pathname !== "/" || !visible || closed) return null;

  return (
    <div
      data-testid="sticky-booking-bar"
      className="fixed bottom-0 inset-x-0 z-40 bg-[hsl(var(--card))]/97 backdrop-blur-lg border-t border-[hsl(var(--accent))]/55 shadow-[0_-12px_40px_rgba(0,0,0,0.12)] animate-in slide-in-from-bottom duration-500"
    >
      <div className="container-x py-3.5 md:py-4">
        <div className="flex items-center justify-between gap-3 md:gap-6 flex-wrap">
          {/* Price + rating (hidden on very small screens to fit CTA) */}
          <div className="flex items-center gap-5 md:gap-8 flex-1 min-w-0">
            <div className="hidden sm:block">
              <div className="text-[10px] tracking-[0.22em] uppercase text-[hsl(var(--accent))] font-semibold">
                {lang === "de" ? "Ab" : "From"}
              </div>
              <div className="font-serif text-2xl md:text-3xl text-[hsl(var(--foreground))] leading-none mt-0.5">
                €300 <span className="text-sm text-[hsl(var(--foreground))]/88 font-sans">/ {lang === "de" ? "Nacht" : "night"}</span>
              </div>
            </div>
            <div className="hidden md:flex items-center gap-2 pr-6 border-r border-[hsl(var(--foreground))]/12">
              <Star className="w-4 h-4 text-[hsl(var(--accent))] fill-[hsl(var(--accent))]" />
              <span className="text-[hsl(var(--foreground))] font-semibold">4.9</span>
              <span className="text-[hsl(var(--foreground))]/88 text-sm">· 47 {lang === "de" ? "Bewertungen" : "reviews"}</span>
            </div>
            <div className="hidden lg:flex items-center gap-2 text-sm text-[hsl(var(--foreground))]/80">
              <Zap className="w-4 h-4 text-[hsl(var(--accent))]" strokeWidth={2} />
              {lang === "de" ? "Sofort buchbar" : "Instant book"}
            </div>
            <div className="text-xs text-[hsl(var(--foreground))]/78 truncate hidden xl:block">
              {lang === "de" ? "Chalet · Landhaus · Combo · Pusterwald, Steiermark" : "Chalet · Landhaus · Combo · Pusterwald, Styria"}
            </div>
          </div>

          {/* CTAs */}
          <div className="flex items-center gap-2 md:gap-3 shrink-0">
            <a
              href="https://wa.me/436641004293"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="sticky-wa"
              className="hidden md:inline-flex text-[11px] tracking-[0.18em] uppercase font-semibold text-[hsl(var(--foreground))]/80 hover:text-[hsl(var(--foreground))] px-3 py-2 transition-colors"
            >
              WhatsApp
            </a>
            <Link
              to="/book"
              data-testid="sticky-book-cta"
              className="btn-primary !py-3 !px-5 md:!px-7 !text-[12px]"
            >
              {lang === "de" ? "Jetzt buchen" : "Book now"} <span aria-hidden>→</span>
            </Link>
            <button
              type="button"
              onClick={() => setClosed(true)}
              data-testid="sticky-close"
              aria-label="Close"
              className="text-[hsl(var(--foreground))]/72 hover:text-[hsl(var(--foreground))] text-lg leading-none px-2"
            >
              ×
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
